import { Controller } from "@hotwired/stimulus"

// 個票 PDF 生成オプション ( 個人情報を隠す / 出力する設問 / 表示順 ) を選ぶ <dialog> モーダル。
// Step1 ( optionsSection ): 設問の選択 + 並べ替え。 右に選択中設問のリアルタイムリスト。
// Step2 ( previewSection ): 1 人分 ( DI ) / 1 セッション分 ( GI ) の PDF プレビューを iframe で全幅表示。
//                          「← 戻って設問を再選択」 で Step1 に戻れる。
// ?pdf=1 付きで遷移してきた場合は connect 時に自動で開く。
export default class extends Controller {
  static targets = [
    "dialog", "form", "questionCheckbox",
    "optionsSection", "previewSection", "previewFrame", "previewSpinner", "previewError",
    "backButton", "previewButton"
  ]
  static values = { previewUrl: String }

  connect() {
    const params = new URLSearchParams(window.location.search)
    if (params.get("pdf") === "1") {
      requestAnimationFrame(() => this.showDialog())
    }
  }

  open(event) {
    if (event) event.preventDefault()
    this.showDialog()
  }

  showDialog() {
    if (this.dialogTarget.open) this.dialogTarget.close()
    this.showOptions()
    this.dialogTarget.showModal()
  }

  cancel(event) {
    event.preventDefault()
    this.dialogTarget.close()
    this.clearPreview()
  }

  // form の submit から「PDF生成」 を確定する直前のバリデーション。
  validate(event) {
    const anyChecked = this.questionCheckboxTargets.some(c => c.checked)
    if (!anyChecked) {
      event.preventDefault()
      window.alert("出力する設問を 1 つ以上選択してください")
    }
  }

  // プレビューボタン押下: Step2 に切り替え、 form 内容を preview_pdf に POST して PDF を iframe に表示。
  async preview(event) {
    event.preventDefault()
    const anyChecked = this.questionCheckboxTargets.some(c => c.checked)
    if (!anyChecked) {
      window.alert("出力する設問を 1 つ以上選択してください")
      return
    }

    this.showPreviewSpinner()
    this.previewErrorTarget.classList.add("hidden")
    this.previewFrameTarget.classList.add("hidden")

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const formData = new FormData(this.formTarget)

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "application/pdf" },
        body: formData
      })

      if (!response.ok) {
        const text = await response.text()
        this.showPreviewError(text || `プレビュー生成に失敗しました ( ${response.status} ) `)
        return
      }

      const blob = await response.blob()
      if (this.lastBlobUrl) URL.revokeObjectURL(this.lastBlobUrl)
      this.lastBlobUrl = URL.createObjectURL(blob)
      this.previewFrameTarget.src = this.lastBlobUrl
      this.previewFrameTarget.classList.remove("hidden")
    } catch (e) {
      this.showPreviewError(`プレビュー生成に失敗しました: ${e.message}`)
    } finally {
      this.hidePreviewSpinner()
    }
  }

  // Step2 から Step1 に戻る。
  hidePreview(event) {
    if (event) event.preventDefault()
    this.showOptions()
  }

  showOptions() {
    if (this.hasOptionsSectionTarget) this.optionsSectionTarget.classList.remove("hidden")
    if (this.hasPreviewSectionTarget) this.previewSectionTarget.classList.add("hidden")
    if (this.hasBackButtonTarget) this.backButtonTarget.classList.add("hidden")
    if (this.hasPreviewButtonTarget) this.previewButtonTarget.classList.remove("hidden")
  }

  showPreviewSpinner() {
    if (this.hasOptionsSectionTarget) this.optionsSectionTarget.classList.add("hidden")
    if (this.hasPreviewSectionTarget) this.previewSectionTarget.classList.remove("hidden")
    // NOTE: Tailwind の hidden と inline-flex の specificity 問題回避のため style.display を直接書く。
    this.previewSpinnerTarget.style.display = "inline-flex"
    if (this.hasBackButtonTarget) this.backButtonTarget.classList.remove("hidden")
    if (this.hasPreviewButtonTarget) this.previewButtonTarget.classList.add("hidden")
  }

  hidePreviewSpinner() {
    this.previewSpinnerTarget.style.display = "none"
  }

  showPreviewError(message) {
    this.hidePreviewSpinner()
    this.previewErrorTarget.textContent = message
    this.previewErrorTarget.classList.remove("hidden")
  }

  clearPreview() {
    if (this.lastBlobUrl) {
      URL.revokeObjectURL(this.lastBlobUrl)
      this.lastBlobUrl = null
    }
    if (this.hasPreviewFrameTarget) {
      this.previewFrameTarget.removeAttribute("src")
      this.previewFrameTarget.classList.add("hidden")
    }
  }
}
